import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Plus, Trash2, ArrowLeft, FileText, AlertTriangle } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { AVAILABLE_SIZES } from "@/lib/constants";
import { ProductSelector } from "@/components/ProductSelector";
import { CustomerSelector } from "@/components/CustomerSelector";
import { ColorBadge } from "@/components/ColorBadge";
import { Table, TableHeader, TableBody, TableCell, TableHead, TableRow } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ShippingAddressSelector } from "@/components/ShippingAddressSelector";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { updateInventoryPendingOrders } from "@/lib/api/inventory";
import { generateOrderNumberOptimistic } from "@/lib/api/orders";
import { USER_DIRECTORY_FIELDS, USERS_DIRECTORY_VIEW, USERS_TABLE } from "@/lib/userAccess";

export function OrderForm({ orderId = null }) {
  const [order, setOrder] = useState({
    date: new Date().toISOString().split('T')[0],
    customerId: "",
    items: [],
    notes: "",
    signature: null,
    fecha_entrega: "",
    shipping_address_id: "",
    forma_pago: [],
    confirmacion_90dias: false,
    discount_percentage: 0,
    payment_transfer: false
  });
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stockAvailable, setStockAvailable] = useState({});
  const [pendingOrders, setPendingOrders] = useState({});
  const { toast } = useToast();
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastX, setLastX] = useState(0);
  const [lastY, setLastY] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userCodRep, setUserCodRep] = useState(null);
  const [canViewAllCustomers, setCanViewAllCustomers] = useState(false);
  const [shippingAddresses, setShippingAddresses] = useState([]);
  const [repName, setRepName] = useState("");
  const [orderNumber, setOrderNumber] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [imagePreviewSrc, setImagePreviewSrc] = useState(null);
  const [imagePreviewAlt, setImagePreviewAlt] = useState("");
  const [isIncidenciasModalOpen, setIsIncidenciasModalOpen] = useState(false);
  const [customerIncidenciasWarning, setCustomerIncidenciasWarning] = useState(null);

  // Gestión de borradores y protección de navegación
  const lastSavedSnapshotRef = useRef(null);
  const previousHashRef = useRef(window.location.hash);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const draftKey = useMemo(() => (orderId ? `orderFormDraft:${orderId}` : 'orderFormDraft:new'), [orderId]);
  const skipRestoreFlag = useMemo(() => (orderId ? `orderFormSkipRestoreOnce:${orderId}` : 'orderFormSkipRestoreOnce:new'), [orderId]);
  const isNewOrder = useMemo(() => !orderId, [orderId]);
  const DRAFT_VERSION = 2;
  const DRAFT_TTL_MS = 24 * 60 * 60 * 1000; // 24h

  const openImagePreview = (src, alt = "Imagen del producto") => {
    setImagePreviewSrc(src);
    setImagePreviewAlt(alt);
    setIsImagePreviewOpen(true);
  };

  // Long press para borrar líneas de una talla
  let longPressTimer = null;

  // Memoizar datos para mejorar rendimiento
  const selectedProductData = useMemo(() => 
    products.find(p => p.id === selectedProduct), 
    [products, selectedProduct]
  );

  const orderTotal = useMemo(() => {
    const total = order.items.reduce((sum, item) => sum + (item.quantity * item.price), 0);
    return Math.round(total * 100) / 100;
  }, [order.items]
  );

  const totalItems = useMemo(() => 
    order.items.reduce((sum, item) => sum + item.quantity, 0), 
    [order.items]
  );

  const isDeliveryDateBeforeOrderDate = (deliveryDate, orderDate) => (
    Boolean(deliveryDate && orderDate && deliveryDate < orderDate)
  );

  useEffect(() => {
    const initializeForm = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          checkUserRole(),
          fetchInitialData()
        ]);
        
        if (orderId) {
          await fetchOrder();
        }
        
        if (canvasRef.current) {
          const canvas = canvasRef.current;
          const ctx = canvas.getContext('2d');
          ctx.strokeStyle = '#000';
          ctx.lineWidth = 2;
          ctx.lineCap = 'round';
        }
      } catch (error) {
        console.error('Error inicializando formulario:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeForm();
  }, [orderId]);

  // Restaurar borrador (solo para pedidos NUEVOS)
  useEffect(() => {
    if (isLoading || !isNewOrder) return;
    // Si hay flag de saltear restauración (después de guardar/cancelar), consumirlo y no preguntar
    try {
      const skipOnce = localStorage.getItem(skipRestoreFlag);
      if (skipOnce) {
        localStorage.removeItem(skipRestoreFlag);
        // Establecer snapshot inicial
        if (lastSavedSnapshotRef.current == null) {
          try { lastSavedSnapshotRef.current = JSON.stringify(order); } catch (_) {}
        }
        return;
      }
    } catch (_) {}

    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        let parsed;
        try { parsed = JSON.parse(raw); } catch (_) { parsed = null; }
        if (!parsed || !parsed.version) {
          // Formato antiguo: limpiar para evitar prompts recurrentes
          try { localStorage.removeItem(draftKey); } catch (_) {}
        } else if (parsed.version === DRAFT_VERSION && parsed.updatedAt && parsed.data) {
          const isFresh = (Date.now() - Number(parsed.updatedAt)) <= DRAFT_TTL_MS;
          if (isFresh) {
            let isDifferent = true;
            try { isDifferent = JSON.stringify(parsed.data) !== JSON.stringify(order); } catch (_) { isDifferent = true; }
            if (isDifferent) {
              const shouldRestore = window.confirm('Hay un borrador reciente sin guardar de este pedido. ¿Quieres restaurarlo?');
              if (shouldRestore) {
                setOrder(parsed.data);
                setHasUnsavedChanges(true);
              }
            }
          } else {
            // TTL vencido: limpiar
            try { localStorage.removeItem(draftKey); } catch (_) {}
          }
        }
      }
    } catch (_) {
      // ignorar errores de parseo/permiso
    }
    // Establecer snapshot inicial si no existe
    if (lastSavedSnapshotRef.current == null) {
      try { lastSavedSnapshotRef.current = JSON.stringify(order); } catch (_) {}
    }
  }, [isLoading, draftKey, skipRestoreFlag, isNewOrder]);

  // Autoguardado del borrador con debounce (solo para pedidos NUEVOS)
  useEffect(() => {
    if (!isNewOrder) return;
    const timer = setTimeout(() => {
      try {
        // Solo autoguardar si hay cambios reales
        const current = JSON.stringify(order);
        if (lastSavedSnapshotRef.current !== current) {
          const payload = { version: DRAFT_VERSION, updatedAt: Date.now(), data: order };
          localStorage.setItem(draftKey, JSON.stringify(payload));
        }
      } catch (_) {}
    }, 500);
    return () => clearTimeout(timer);
  }, [order, draftKey, isNewOrder]);

  // Detectar cambios no guardados respecto al snapshot
  useEffect(() => {
    try {
      const current = JSON.stringify(order);
      setHasUnsavedChanges(lastSavedSnapshotRef.current !== current);
    } catch (_) {
      setHasUnsavedChanges(true);
    }
  }, [order]);

  // Proteger cierre/recarga de pestaña
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!hasUnsavedChanges) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Interceptar cambios de hash para confirmar salida
  const ignoreNextHashChangeRef = useRef(false);
  useEffect(() => {
    const handleHashChange = () => {
      if (ignoreNextHashChangeRef.current) {
        ignoreNextHashChangeRef.current = false;
        previousHashRef.current = window.location.hash;
        return;
      }
      if (hasUnsavedChanges) {
        const confirmLeave = window.confirm('Hay cambios sin guardar. Si sales, perderás los cambios.');
        if (!confirmLeave) {
          ignoreNextHashChangeRef.current = true;
          window.location.hash = previousHashRef.current || '#/pedidos';
        } else {
          try { localStorage.removeItem(draftKey); } catch (_) {}
          previousHashRef.current = window.location.hash;
        }
      } else {
        previousHashRef.current = window.location.hash;
      }
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [hasUnsavedChanges, draftKey]);

  const navigateBackSafe = () => {
    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm('Hay cambios sin guardar. ¿Quieres salir y descartarlos?');
      if (!confirmLeave) return;
    }
    try { localStorage.removeItem(draftKey); } catch (_) {}
    try { localStorage.setItem(skipRestoreFlag, '1'); } catch (_) {}
    setHasUnsavedChanges(false);
    try { lastSavedSnapshotRef.current = JSON.stringify(order); } catch (_) {}
    window.location.hash = "#/pedidos";
  };

  const checkUserRole = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return;
      }

      const { data: userData, error } = await supabase
        .from(USERS_TABLE)
        .select('role, cod_rep, ver_comisiones_globales')
        .eq('id', user.id)
        .maybeSingle();

      if (error) throw error;
      
      if (userData) {
        setUserRole(userData.role);
        setUserCodRep(userData.cod_rep);
        setCanViewAllCustomers(userData.ver_comisiones_globales || userData.role === 'admin');
      }
    } catch (error) {
      console.error('Error verificando rol:', error);
      setUserRole('cliente');
    }
  };

  const fetchInitialData = async () => {
    try {
      // Cargar productos y clientes en paralelo
      const [productsResult, customersResult] = await Promise.all([
        supabase
          .from('products')
          .select('*')
          .eq('active', true),
        supabase
          .from('customers')
          .select('*')
          .order('name', { ascending: true })
      ]);

      if (productsResult.error) throw productsResult.error;
      if (customersResult.error) throw customersResult.error;

      setProducts(productsResult.data || []);
      setCustomers(customersResult.data || []);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los datos iniciales",
        variant: "destructive"
      });
    }
  };

  const fetchOrder = async () => {
    if (!orderId) return;

    try {
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select(`
          *,
          customer:customers(
            id,
            name,
            code,
            email,
            phone,
            street,
            city,
            province,
            "postalCode",
            cod_rep,
            incidencias
          ),
          items:order_items(
            id,
            product_id,
            size,
            color,
            quantity,
            pending_quantity,
            price,
            product:products(
              id,
              code,
              description,
              price,
              images,
              main_image_index,
              colors,
              sizes
            )
          ),
          shipping_address:shipping_addresses(*)
        `)
        .eq('id', orderId)
        .single();

      if (orderError) throw orderError;

      if (orderData) {
        setOrderNumber(orderData.number ?? orderData.id);
        
        // Cargar las direcciones de envío del cliente
        const { data: addresses, error: addressesError } = await supabase
          .from('shipping_addresses')
          .select('*')
          .eq('customer_id', orderData.customer_id)
          .order('is_default', { ascending: false });

        if (!addressesError) {
          setShippingAddresses(addresses || []);
        }

        // Mapear los items del pedido correctamente
        const mappedItems = orderData.items.map(item => {
          const originalPrice = item.product.price;
          const discountMultiplier = orderData.discount_percentage > 0 ? (1 - (orderData.discount_percentage / 100)) : 1;
          const finalPrice = Math.round(originalPrice * discountMultiplier * 100) / 100;
          
          return {
            id: item.id,
            product_id: item.product_id,
            product_code: item.product.code,
            description: item.product.description,
            size: item.size,
            color: item.color,
            quantity: item.quantity,
            pending_quantity: item.pending_quantity,
            price: finalPrice,
            original_price: originalPrice, // Precio original del producto
            product: item.product
          };
        });

        const newOrder = {
          id: orderData.id,
          date: new Date(orderData.date).toISOString().split('T')[0],
          customerId: orderData.customer_id,
          customer: orderData.customer,
          items: mappedItems,
          total: orderData.total,
          status: orderData.status,
          notes: orderData.notes || "",
          fecha_entrega: orderData.fecha_entrega ? new Date(orderData.fecha_entrega).toISOString().split('T')[0] : null,
          shipping_address_id: orderData.shipping_address_id,
          shipping_address: orderData.shipping_address,
          forma_pago: orderData.forma_pago || [30],
          confirmacion_90dias: orderData.confirmacion_90dias || false,
          signature: orderData.signature,
          discount_percentage: orderData.discount_percentage || 0,
          payment_transfer: orderData.payment_transfer || false
        };
        setOrder(newOrder);
        try { lastSavedSnapshotRef.current = JSON.stringify(newOrder); } catch (_) {}
        setHasUnsavedChanges(false);

        // Establecer nombre de representante basado en cod_rep del pedido o del cliente
        try {
          const effectiveCodRep = String(orderData.cod_rep || orderData.customer?.cod_rep || '');
          if (effectiveCodRep) {
            const { data: reps } = await supabase
              .from(USERS_DIRECTORY_VIEW)
              .select(USER_DIRECTORY_FIELDS)
              .eq('cod_rep', effectiveCodRep);
            const names = (reps || []).map(r => r.name).filter(Boolean);
            setRepName(names.join(', '));
          } else {
            setRepName("");
          }
        } catch (_) {
          setRepName("");
        }
      }
    } catch (error) {
      console.error('Error cargando pedido:', error);
      toast({
        title: "Error",
        description: "No se pudo cargar el pedido",
        variant: "destructive"
      });
    }
  };

  // Optimizar la verificación de stock con debounce
  const checkStockAvailability = useCallback(async (productId, color, size) => {
    if (!productId || !color || !size) return null;
    
    try {
      const { data: inventoryData, error: inventoryError } = await supabase
        .from('inventory')
        .select('quantity')
        .eq('product_id', productId)
        .eq('size', size)
        .eq('color', color)
        .single();

      if (inventoryError) throw inventoryError;
      return inventoryData?.quantity || 0;
    } catch (error) {
      console.error('Error al verificar stock:', error);
      return 0;
    }
  }, []);

  const handleAddItem = async (size) => {
    const product = products.find(p => p.id === selectedProduct);
    if (!product || !selectedColor || !size) return;

    try {
      // Verificar si el producto requiere stock
      if (product.venta_solo_stock) {
        const availableStock = await checkStockAvailability(selectedProduct, selectedColor, size);
        
        // Si no hay stock o es 0, mostrar error
        if (availableStock <= 0) {
          toast({
            title: "Error",
            description: "Este producto solo se puede vender con stock disponible",
            variant: "destructive"
          });
          return;
        }

        // Verificar si ya existe el ítem y calcular la cantidad total
        const existingItem = order.items.find(item =>
          item.product_id === selectedProduct &&
          item.color === selectedColor &&
          item.size === size
        );

        const currentQuantity = existingItem ? existingItem.quantity : 0;
        const newQuantity = currentQuantity + 1;
        
        // Verificar si la nueva cantidad excede el stock disponible
        if (newQuantity > availableStock) {
          toast({
            title: "Error",
            description: `Stock insuficiente. Solo hay ${availableStock} unidades disponibles`,
            variant: "destructive"
          });
          return;
        }
      }

      // Validación extra: no añadir si falta algún dato clave
      if (!product.code || !product.description || !selectedColor || !size) return;

      setOrder(prev => {
        // Buscar si ya existe una línea igual
        const existingIndex = prev.items.findIndex(item =>
          item.product_id === selectedProduct &&
          item.color === selectedColor &&
          item.size === size
        );
        if (existingIndex !== -1) {
          // Si existe, sumar 1 a la cantidad y pending_quantity
          const updatedItems = [...prev.items];
          const updatedItem = {
            ...updatedItems[existingIndex],
            quantity: updatedItems[existingIndex].quantity + 1,
            pending_quantity: updatedItems[existingIndex].pending_quantity + 1
          };
          // Eliminar el ítem de su posición actual
          updatedItems.splice(existingIndex, 1);
          // Añadir el ítem actualizado al principio
          return { ...prev, items: [updatedItem, ...updatedItems] };
        } else {
          // Si no existe, crear nueva línea SOLO si todos los datos son válidos
          if (product.code && product.description && selectedColor && size) {
            // Aplicar descuento si existe
            const discountMultiplier = order.discount_percentage > 0 ? (1 - (order.discount_percentage / 100)) : 1;
            const finalPrice = Math.round(product.price * discountMultiplier * 100) / 100;
            
            const newItem = {
              product_id: selectedProduct,
              product_code: product.code,
              description: product.description,
              size,
              color: selectedColor,
              quantity: 1,
              pending_quantity: 1,
              price: finalPrice,
              original_price: product.price
            };
            return { ...prev, items: [newItem, ...prev.items] };
          } else {
            return prev;
          }
        }
      });
    } catch (error) {
      console.error('Error al verificar stock:', error);
      toast({
        title: "Error",
        description: "No se pudo verificar el stock del producto",
        variant: "destructive"
      });
    }
  };

  const handleColorSelect = async (color) => {
    setSelectedColor(color === selectedColor ? "" : color);
    
    // Si hay un producto seleccionado y el producto requiere stock, verificar el stock disponible
    if (selectedProduct) {
      const product = products.find(p => p.id === selectedProduct);
      if (product?.venta_solo_stock) {
        try {
          const { data: inventoryData, error: inventoryError } = await supabase
            .from('inventory')
            .select('product_id, size, color, quantity, pending_orders')
            .eq('product_id', selectedProduct)
            .eq('color', color);

          if (inventoryError) throw inventoryError;

          // Crear un mapa de stock disponible por talla
          const stockMap = {};
          const pendingMap = {};
          product.sizes.forEach(size => {
            const item = inventoryData?.find(
              inv => inv.size === size && inv.color === color
            );
            stockMap[size] = item?.quantity || 0;
            pendingMap[size] = item?.pending_orders || 0;
          });
          setStockAvailable(stockMap);
          setPendingOrders(pendingMap);
        } catch (error) {
          console.error('Error al verificar stock:', error);
          setStockAvailable({});
          setPendingOrders({});
        }
      } else {
        setStockAvailable({});
        setPendingOrders({});
      }
    }
  };

  const handleProductSelect = async (productId) => {
    setSelectedProduct(productId);
    setSelectedColor("");
    setStockAvailable({});
  };

  const handleRemoveItem = (index) => {
    setOrder(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleSizeMouseDown = (size) => {
    longPressTimer = setTimeout(() => {
      // Borrar todas las líneas de ese producto, color y talla
      setOrder(prev => ({
        ...prev,
        items: prev.items.filter(item =>
          !(item.product_id === selectedProduct && item.color === selectedColor && item.size === size)
        )
      }));
    }, 600); // 600ms para considerar long press
  };

  const handleSizeMouseUp = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      longPressTimer = null;
    }
  };

  const startDrawing = (e) => {
    // Soporte para Pointer Events (táctil, lápiz y ratón)
    if (e?.preventDefault) e.preventDefault();
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX ?? 0) - rect.left;
    const y = (e.clientY ?? 0) - rect.top;

    // Capturar el puntero para seguir dibujando aunque salga del canvas
    if (e?.target?.setPointerCapture && typeof e.pointerId === 'number') {
      try { e.target.setPointerCapture(e.pointerId); } catch (_) {}
    }

    setIsDrawing(true);
    setLastX(x);
    setLastY(y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    if (e?.preventDefault) e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX ?? 0) - rect.left;
    const y = (e.clientY ?? 0) - rect.top;

    ctx.beginPath();
    ctx.moveTo(lastX, lastY);
    ctx.lineTo(x, y);
    ctx.stroke();

    setLastX(x);
    setLastY(y);
  };

  const stopDrawing = (e) => {
    if (e?.preventDefault) e.preventDefault();
    // Liberar captura del puntero si aplica
    if (e?.target?.releasePointerCapture && typeof e.pointerId === 'number') {
      try { e.target.releasePointerCapture(e.pointerId); } catch (_) {}
    }
    setIsDrawing(false);
    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL('image/png');
    setOrder(prev => ({ ...prev, signature: signatureData }));
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setOrder(prev => ({ ...prev, signature: null }));
  };

  const handlePaymentChange = (dias) => {
    setOrder(prev => {
      const currentPayments = prev.forma_pago || [];
      
      // Si es 30 días
      if (dias === 30) {
        if (currentPayments.includes(30)) {
          // Si ya está seleccionado, lo quitamos (queda contado)
          return {
            ...prev,
            forma_pago: [],
            confirmacion_90dias: false
          };
        } else {
          // Si no está seleccionado, lo añadimos
          return {
            ...prev,
            forma_pago: [30],
            confirmacion_90dias: false
          };
        }
      }

      // Si es 60 días
      if (dias === 60) {
        if (currentPayments.includes(60)) {
          // Si ya está seleccionado, lo quitamos
          return {
            ...prev,
            forma_pago: currentPayments.filter(d => d !== 60),
            confirmacion_90dias: false
          };
        } else {
          // Solo se puede añadir si 30 está seleccionado
          if (currentPayments.includes(30)) {
            return {
              ...prev,
              forma_pago: [...currentPayments, 60].sort((a, b) => a - b),
              confirmacion_90dias: false
            };
          }
        }
      }

      // Si es 90 días
      if (dias === 90) {
        if (currentPayments.includes(90)) {
          // Si ya está seleccionado, lo quitamos
          return {
            ...prev,
            forma_pago: currentPayments.filter(d => d !== 90),
            confirmacion_90dias: false
          };
        } else {
          // Solo se puede añadir si 30 y 60 están seleccionados
          if (currentPayments.includes(30) && currentPayments.includes(60)) {
            return {
              ...prev,
              forma_pago: [...currentPayments, 90].sort((a, b) => a - b),
              confirmacion_90dias: false
            };
          }
        }
      }

      return prev;
    });
  };

  const handleCustomerChange = async (customerId) => {
    if (!customerId) {
      setOrder(prev => ({ 
        ...prev, 
        customerId: null, 
        customer: null,
        shipping_address_id: "", 
        shipping_address: null 
      }));
      setShippingAddresses([]);
      setRepName("");
      setCustomerIncidenciasWarning(null);
      setIsIncidenciasModalOpen(false);
      return;
    }

    // Obtener el código del cliente seleccionado
    const selectedCustomer = customers.find(c => c.id === customerId);
    if (!selectedCustomer) return;

    const incidencias = selectedCustomer.incidencias?.trim();
    if (incidencias) {
      setCustomerIncidenciasWarning({
        customerName: selectedCustomer.name,
        incidencias
      });
      setIsIncidenciasModalOpen(true);
    } else {
      setCustomerIncidenciasWarning(null);
      setIsIncidenciasModalOpen(false);
    }

    try {
      // Cargar direcciones de envío del cliente usando customer_id
      const { data: addresses, error } = await supabase
        .from('shipping_addresses')
        .select('*')
        .eq('customer_id', customerId)
        .order('is_default', { ascending: false });

      if (error) throw error;

      let defaultAddressId = "none";
      let defaultAddress = null;

      if (addresses && addresses.length > 0) {
        setShippingAddresses(addresses);
        // Buscar la dirección marcada como predeterminada
        defaultAddress = addresses.find(addr => addr.is_default === true);
        if (defaultAddress) {
          defaultAddressId = defaultAddress.id;
        } else {
          // Si no hay dirección predeterminada, usar "none"
          defaultAddressId = "none";
          defaultAddress = null;
        }
      } else {
        setShippingAddresses([]);
      }

      // Actualizar el estado del pedido con la dirección por defecto
      setOrder(prev => ({
        ...prev,
        customerId,
        customer: selectedCustomer,
        shipping_address_id: defaultAddressId,
        shipping_address: defaultAddress
      }));

      // Obtener el nombre del representante (manejar múltiples resultados y tipos)
      if (selectedCustomer.cod_rep) {
        const effectiveCodRep = String(selectedCustomer.cod_rep);
        const { data: reps, error: userError } = await supabase
          .from(USERS_DIRECTORY_VIEW)
          .select(USER_DIRECTORY_FIELDS)
          .eq('cod_rep', effectiveCodRep);

        if (!userError && reps && reps.length > 0) {
          // Si hubiera varios reps con mismo código, concatenar nombres
          const names = reps.map(r => r.name).filter(Boolean);
          setRepName(names.join(', '));
        } else {
          setRepName("");
        }
      } else {
        setRepName("");
      }
    } catch (error) {
      console.error('Error al cargar datos del cliente:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los datos del cliente",
        variant: "destructive"
      });
    }
  };

  // Optimizar el guardado con transacciones y operaciones en lote
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevenir múltiples envíos
    if (isSubmitting) {
      return;
    }

    if (!order.customerId) {
      toast({
        title: "Error",
        description: "Debe seleccionar un cliente",
        variant: "destructive"
      });
      return;
    }

    if (order.items.length === 0) {
      toast({
        title: "Error",
        description: "Debe agregar al menos un ítem",
        variant: "destructive"
      });
      return;
    }

    if (isDeliveryDateBeforeOrderDate(order.fecha_entrega, order.date)) {
      toast({
        title: "Error en fechas",
        description: "La fecha de entrega debe ser mayor o igual que la fecha del pedido",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Insert or update order
      const orderData = {
        date: order.date,
        customer_id: order.customerId,
        total: orderTotal,
        status: 'pending',
        notes: order.notes || "",
        fecha_entrega: order.fecha_entrega || null,
        shipping_address_id: !order.shipping_address_id || order.shipping_address_id === "none" ? null : order.shipping_address_id,
        forma_pago: order.forma_pago || [],
        confirmacion_90dias: order.confirmacion_90dias || false,
        signature: order.signature,
        preparation_status: 'pending',
        discount_percentage: order.discount_percentage || 0,
        payment_transfer: order.payment_transfer || false
      };

      // Solo añadimos created_at y user_id si es un nuevo pedido
      if (!orderId) {
        orderData.created_at = new Date().toISOString();
        orderData.user_id = (await supabase.auth.getUser()).data.user.id;
      } else {
        delete orderData.date;
      }

      let orderResult;
      if (orderId) {
        // Optimizar: obtener datos del cliente y items anteriores en paralelo
        const [customerResult, oldItemsResult] = await Promise.all([
          supabase
            .from('customers')
            .select('cod_rep')
            .eq('id', order.customerId)
            .single(),
          supabase
            .from('order_items')
            .select('product_id, size, color, pending_quantity')
            .eq('order_id', orderId)
        ]);

        if (customerResult.error) throw customerResult.error;
        if (oldItemsResult.error) throw oldItemsResult.error;

        orderData.cod_rep = customerResult.data.cod_rep;

        // Restar cantidades anteriores del inventario en lote
        if (oldItemsResult.data && oldItemsResult.data.length > 0) {
          const inventoryUpdates = oldItemsResult.data.map(item => ({
            product_id: item.product_id,
            size: item.size,
            color: item.color,
            pending_orders: item.pending_quantity || 0
          }));

          // Usar la nueva función que resta correctamente los valores
          await updateInventoryPendingOrders(inventoryUpdates, 'subtract');
        }

        const { data, error } = await supabase
          .from('orders')
          .update(orderData)
          .eq('id', orderId)
          .select()
          .single();

        if (error) throw error;
        orderResult = data;

        // Eliminamos los items anteriores
        const { error: deleteError } = await supabase
          .from('order_items')
          .delete()
          .eq('order_id', orderId);

        if (deleteError) throw deleteError;
      } else {
        // Para nuevos pedidos, generar número de pedido y obtener cod_rep del cliente
        const [orderNumber, customerData] = await Promise.all([
          generateOrderNumberOptimistic(),
          supabase
            .from('customers')
            .select('cod_rep')
            .eq('id', order.customerId)
            .single()
        ]);

        if (customerData.error) throw customerData.error;
        orderData.cod_rep = customerData.data.cod_rep;
        orderData.number = orderNumber; // Asignar el número generado

        const { data, error } = await supabase
          .from('orders')
          .insert(orderData)
          .select()
          .single();

        if (error) throw error;
        orderResult = data;
      }

      // Insertar nuevos items en lote
      const orderItems = order.items.map(item => ({
        order_id: orderResult.id,
        product_id: item.product_id,
        size: item.size,
        color: item.color,
        quantity: item.quantity,
        pending_quantity: item.quantity,
        price: item.price,
        created_at: new Date().toISOString()
      }));

      const { error: itemsError } = await supabase
        .from('order_items')
        .insert(orderItems);

      if (itemsError) throw itemsError;

      // Actualizar pending_orders en inventario en lote
      if (orderItems.length > 0) {
        const inventoryUpdates = orderItems.map(item => ({
          product_id: item.product_id,
          size: item.size,
          color: item.color,
          pending_orders: item.pending_quantity
        }));

        // Usar la nueva función que suma correctamente los valores
        await updateInventoryPendingOrders(inventoryUpdates, 'add');
      }

      toast({
        title: "Éxito",
        description: "Pedido guardado exitosamente",
      });
      try { localStorage.removeItem(draftKey); } catch (_) {}
      try { localStorage.setItem(skipRestoreFlag, '1'); } catch (_) {}
      setHasUnsavedChanges(false);
      try { lastSavedSnapshotRef.current = JSON.stringify(order); } catch (_) {}
      window.location.hash = "#/pedidos";
    } catch (error) {
      console.error('Error saving order:', error);
      toast({
        title: "Error",
        description: "No se pudo guardar el pedido",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando formulario...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con número de pedido */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={navigateBackSafe}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver a Pedidos
          </Button>
        </div>
        
        {orderId && orderNumber && (
          <div className="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-lg border">
            <FileText className="h-5 w-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">
              Pedido #{orderNumber}
            </span>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-lg shadow-sm border border-blue-200">
          <div className="flex items-center gap-2 mb-6">
            <span className="text-blue-600 text-xl">📋</span>
            <h3 className="text-lg font-semibold text-gray-800">Información del Pedido</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Cliente */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-blue-100">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-blue-600">👤</span>
                <label htmlFor="customer-selector" className="block text-sm font-semibold text-gray-700">Cliente</label>
              </div>
              <CustomerSelector
                id="customer-selector"
                value={order.customerId}
                onSelect={(customerId) => handleCustomerChange(customerId)}
              />
              {order.customerId && (
                <div className="mt-2 p-2 bg-blue-50 rounded-md border border-blue-200">
                  <div className="text-sm text-blue-700 font-medium">Rep: {repName}</div>
                </div>
              )}
            </div>

            {/* Fechas */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-green-100">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-green-600">📅</span>
                <span className="text-sm font-semibold text-gray-700">Fechas</span>
              </div>
              <div className="space-y-3">
                <div>
                  <label htmlFor="order-date" className="block text-xs font-medium text-gray-600 mb-1">Fecha del Pedido</label>
                  <Input
                    id="order-date"
                    name="order-date"
                    type="date"
                    required
                    value={order.date}
                    disabled
                    className="border-green-200 focus:border-green-500 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label htmlFor="fecha-entrega" className="block text-xs font-medium text-gray-600 mb-1">Fecha de Entrega</label>
                  <Input
                    id="fecha-entrega"
                    name="fecha-entrega"
                    type="date"
                    value={order.fecha_entrega}
                    min={order.date}
                    onChange={(e) => setOrder(prev => ({ ...prev, fecha_entrega: e.target.value }))}
                    className="border-green-200 focus:border-green-500 focus:ring-green-500"
                  />
                </div>
              </div>
            </div>

            {/* Formas de Pago */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-purple-100 md:col-span-2">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-purple-600">💳</span>
                <label className="block text-sm font-semibold text-gray-700">Formas de Pago</label>
              </div>
              <div className="flex gap-2 mb-3">
                {[30, 60, 90].map((dias) => {
                  const isSelected = order.forma_pago?.includes(dias);
                  const is90Days = dias === 90;
                  const isConfirmed = order.confirmacion_90dias;
                  
                  let buttonVariant = "outline";
                  let buttonClassName = "flex items-center gap-2 transition-all duration-200";
                  
                  if (isSelected) {
                    if (is90Days) {
                      if (isConfirmed) {
                        buttonVariant = "default";
                        buttonClassName += " bg-green-600 hover:bg-green-700 shadow-lg";
                      } else {
                        buttonVariant = "default";
                        buttonClassName += " bg-red-600 hover:bg-red-700 shadow-lg";
                      }
                    } else {
                      buttonVariant = "default";
                      buttonClassName += " bg-purple-600 hover:bg-purple-700 shadow-lg";
                    }
                  }
                  
                  // Determinar si el botón debe estar deshabilitado
                  let isDisabled = false;
                  if (dias === 60 && !order.forma_pago?.includes(30)) {
                    isDisabled = true;
                  } else if (dias === 90 && (!order.forma_pago?.includes(30) || !order.forma_pago?.includes(60))) {
                    isDisabled = true;
                  }
                  
                  return (
                    <Button
                      key={dias}
                      type="button"
                      variant={buttonVariant}
                      onClick={() => handlePaymentChange(dias)}
                      className={`${buttonClassName} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      disabled={isDisabled}
                    >
                      {dias} días
                    </Button>
                  );
                })}
              </div>
              {order.forma_pago?.includes(90) && (
                <div className="mt-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={order.confirmacion_90dias}
                      onChange={(e) => setOrder(prev => ({ ...prev, confirmacion_90dias: e.target.checked }))}
                      disabled={userRole !== 'admin'}
                      className="rounded border-gray-300"
                    />
                    <span className={userRole !== 'admin' ? 'text-gray-500' : 'text-yellow-700 font-medium'}>
                      {userRole === 'admin' ? 'Confirmar pago a 90 días' : 'Pendiente de confirmación por administrador'}
                    </span>
                  </label>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Selecciona las formas de pago en orden. 60 días requiere 30 días, 90 días requiere 30 y 60 días. Si no seleccionas ninguna, será pago al contado.
              </p>
              
              {/* Descuento Global */}
              <div className="mt-4 pt-4 border-t border-purple-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-purple-600">💰</span>
                  <label className="block text-sm font-semibold text-gray-700">Descuento Global (%)</label>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={order.discount_percentage || 0}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      setOrder(prev => {
                        const newOrder = { ...prev, discount_percentage: value };
                        
                        // Recalcular precios de todos los items si hay descuento
                        if (value > 0) {
                          const discountMultiplier = 1 - (value / 100);
                          newOrder.items = prev.items.map(item => ({
                            ...item,
                            price: Math.round(item.original_price * discountMultiplier * 100) / 100
                          }));
                        } else {
                          // Restaurar precios originales si no hay descuento
                          newOrder.items = prev.items.map(item => ({
                            ...item,
                            price: Math.round((item.original_price || item.price) * 100) / 100
                          }));
                        }
                        
                        return newOrder;
                      });
                    }}
                    className="w-24 border-purple-200 focus:border-purple-500 focus:ring-purple-500"
                    placeholder="0"
                  />
                  <span className="text-sm text-gray-600">%</span>
                  {order.discount_percentage > 0 && (
                    <span className="text-sm text-green-600 font-medium">
                      -${Math.round((order.items.reduce((sum, item) => sum + (item.quantity * item.original_price), 0) * order.discount_percentage / 100) * 100) / 100}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Pago por Transferencia */}
              <div className="mt-4 pt-4 border-t border-purple-200">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={order.payment_transfer || false}
                    onChange={(e) => setOrder(prev => ({ ...prev, payment_transfer: e.target.checked }))}
                    className="rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-purple-700 font-medium">Pago mediante transferencia</span>
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Marca esta opción si el pago se efectúa mediante transferencia bancaria
                </p>
              </div>
            </div>

            {/* Dirección de Envío */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-orange-100">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-orange-600">🚚</span>
                <Label htmlFor="shipping-address" className="text-sm font-semibold text-gray-700">Dirección de Envío</Label>
              </div>
              <Select
                id="shipping-address"
                value={order.shipping_address_id || "none"}
                onValueChange={(value) => {
                  if (value === "none") {
                    setOrder(prev => ({
                      ...prev,
                      shipping_address_id: "none",
                      shipping_address: null
                    }));
                  } else {
                    const selectedAddress = shippingAddresses.find(addr => addr.id === value);
                    setOrder(prev => ({
                      ...prev,
                      shipping_address_id: value,
                      shipping_address: selectedAddress
                    }));
                  }
                }}
              >
                <SelectTrigger className="border-orange-200 focus:border-orange-500 focus:ring-orange-500">
                  <SelectValue>
                    {order.shipping_address_id === "" || order.shipping_address_id === "none" 
                      ? "Sin dirección de envío" 
                      : shippingAddresses.find(addr => addr.id === order.shipping_address_id)?.name || "Seleccionar dirección de envío"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin dirección de envío</SelectItem>
                  {shippingAddresses.map((address) => (
                    <SelectItem key={address.id} value={address.id}>
                      {`${address.name} - ${address.street}, ${address.city} (${address.postal_code})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Observaciones */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-red-100">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-red-600">📝</span>
                <label htmlFor="order-notes" className="block text-sm font-semibold text-gray-700">Observaciones</label>
              </div>
              <textarea
                id="order-notes"
                name="order-notes"
                className="w-full min-h-[100px] p-3 border border-red-200 rounded-md focus:border-red-500 focus:ring-red-500 resize-none"
                placeholder="Observaciones adicionales..."
                value={order.notes || ""}
                onChange={(e) => setOrder(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>
        </div>

      {/* Resumen del pedido optimizado */}
      {order.items.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-green-50 p-6 rounded-lg shadow-md border border-blue-200 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <h3 className="text-lg font-semibold text-gray-800">Resumen del Pedido</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-blue-100">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-blue-600">👕</span>
                <span className="text-sm font-medium text-gray-600">Total Prendas</span>
              </div>
              <span className="text-2xl font-bold text-blue-600">
                {totalItems}
              </span>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-green-100">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-green-600">💰</span>
                <span className="text-sm font-medium text-gray-600">Importe Total</span>
              </div>
              <span className="text-2xl font-bold text-green-600">
                {orderTotal.toFixed(2)}€
              </span>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-purple-100">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-purple-600">📋</span>
                <span className="text-sm font-medium text-gray-600">Líneas de Pedido</span>
              </div>
              <span className="text-2xl font-bold text-purple-600">
                {order.items.length}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
          <Plus className="h-5 w-5 text-blue-600" />
          Agregar Productos al Pedido
        </h3>
        <div className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <label htmlFor="product-selector" className="block text-sm font-medium mb-2 text-blue-800">1. Seleccionar Producto</label>
            <ProductSelector
              id="product-selector"
              value={selectedProduct}
              onSelect={(productId) => {
                handleProductSelect(productId);
              }}
            />
          </div>

          {selectedProductData && (
            <>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <label htmlFor="color-selector" className="block text-sm font-medium mb-3 text-green-800">2. Seleccionar Color</label>
                <div id="color-selector" className="flex flex-wrap gap-3" role="group" aria-labelledby="color-selector">
                  {selectedProductData.colors.map((color) => (
                    <Button
                      key={color}
                      type="button"
                      variant={selectedColor === color ? "default" : "outline"}
                      onClick={() => handleColorSelect(color)}
                      className={`flex items-center gap-2 transition-all duration-200 ${
                        selectedColor === color 
                          ? 'bg-green-600 hover:bg-green-700 shadow-lg scale-105' 
                          : 'hover:bg-green-100 hover:border-green-300'
                      }`}
                      aria-pressed={selectedColor === color}
                    >
                      <ColorBadge color={color} />
                    </Button>
                  ))}
                </div>
              </div>
              {selectedColor && (
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <label htmlFor="size-selector" className="block text-sm font-medium mb-3 text-orange-800">
                    3. Agregar Tallas al Pedido
                  </label>
                  <div className="mb-3">
                    <div className="flex items-center gap-2 text-sm text-orange-700">
                      <Plus className="h-4 w-4" />
                      <span>Haz clic en las tallas para agregarlas al pedido</span>
                    </div>
                  </div>
                  <div id="size-selector" className="flex flex-wrap gap-3" role="group" aria-labelledby="size-selector">
                    {selectedProductData.sizes.map((size) => {
                      const hasStock = selectedProductData.venta_solo_stock ? (stockAvailable[size] > 0) : true;
                      const currentQuantity = order.items.find(
                        item => item.product_id === selectedProduct && 
                               item.color === selectedColor && 
                               item.size === size
                      )?.quantity || 0;
                      const pendingQuantity = pendingOrders[size] || 0;
                      const realStock = stockAvailable[size] - pendingQuantity;
                      const canAddMore = hasStock && (!selectedProductData.venta_solo_stock || currentQuantity < realStock);

                      return (
                        <Button
                          key={size}
                          type="button"
                          variant={canAddMore ? "outline" : "outline"}
                          onClick={() => handleAddItem(size)}
                          onMouseDown={() => handleSizeMouseDown(size)}
                          onMouseUp={handleSizeMouseUp}
                          onMouseLeave={handleSizeMouseUp}
                          disabled={!canAddMore}
                          className={`relative min-w-[70px] h-14 transition-all duration-200 ${
                            !canAddMore 
                              ? 'opacity-50 cursor-not-allowed bg-gray-100' 
                              : 'hover:bg-orange-100 hover:border-orange-400 hover:shadow-md hover:scale-105 active:scale-95'
                          } ${currentQuantity > 0 ? 'bg-orange-100 border-orange-400 shadow-md' : ''}`}
                          aria-label={`Añadir talla ${size}`}
                        >
                          <div className="flex flex-col items-center gap-1">
                            <span className="text-lg font-bold">{size}</span>
                            {currentQuantity > 0 && (
                              <span className="text-xs bg-orange-600 text-white rounded-full px-2 py-0.5 font-medium">
                                {currentQuantity}
                              </span>
                            )}
                          </div>
                          {selectedProductData.venta_solo_stock && (
                            <span className={`absolute -top-2 -right-2 text-xs rounded-full px-2 py-1 font-bold ${
                              realStock <= 0 
                                ? 'bg-red-100 text-red-700 border border-red-300' 
                                : 'bg-green-100 text-green-700 border border-green-300'
                            }`}>
                              {realStock}
                            </span>
                          )}
                        </Button>
                      );
                    })}
                  </div>
                  <div className="text-sm text-orange-700 mt-3 p-3 bg-orange-100 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">💡 Información:</span>
                    </div>
                    {selectedProductData.venta_solo_stock 
                      ? "Los números en las esquinas indican el stock disponible (stock - pedidos pendientes). Las tallas con números verdes están disponibles."
                      : "Haz clic en una talla para agregarla al pedido con cantidad 1. Las tallas con números naranjas indican que ya están en el pedido."}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal de vista previa de imagen */}
      <Dialog open={isImagePreviewOpen} onOpenChange={setIsImagePreviewOpen}>
        <DialogContent className="max-w-[90vw] md:max-w-3xl p-2 bg-black">
          {imagePreviewSrc && (
            <div className="w-full h-full flex items-center justify-center">
              <img
                src={imagePreviewSrc}
                alt={imagePreviewAlt}
                className="max-h-[80vh] max-w-full object-contain"
                draggable={false}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={isIncidenciasModalOpen} onOpenChange={setIsIncidenciasModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Incidencias del cliente
            </DialogTitle>
            <DialogDescription>
              Revise esta advertencia antes de continuar con el pedido.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {customerIncidenciasWarning?.customerName && (
              <p className="text-sm font-medium text-gray-800">
                {customerIncidenciasWarning.customerName}
              </p>
            )}
            <div className="max-h-64 overflow-auto whitespace-pre-wrap rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
              {customerIncidenciasWarning?.incidencias}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" onClick={() => setIsIncidenciasModalOpen(false)}>
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="mt-8">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-lg font-semibold text-gray-800">📋 Productos en el Pedido</span>
            <span className="text-sm text-gray-500">({order.items.length} líneas)</span>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold text-gray-700">Producto</TableHead>
                  <TableHead className="font-semibold text-gray-700">Imagen</TableHead>
                  <TableHead className="font-semibold text-gray-700">Color</TableHead>
                  <TableHead className="font-semibold text-gray-700">Tallas</TableHead>
                  <TableHead className="font-semibold text-gray-700">Precio</TableHead>
                  <TableHead className="font-semibold text-gray-700">Subtotal</TableHead>
                  <TableHead className="text-right font-semibold text-gray-700">Acciones</TableHead>
                </TableRow>
              </TableHeader>
            <TableBody>
              {(() => {
                // Agrupar items por producto y color
                const groupedItems = order.items
                  .filter(item => item.product_id && item.product_code && item.description && item.size && item.color && item.quantity > 0)
                  .reduce((acc, item) => {
                    const key = `${item.product_id}-${item.color}`;
                    if (!acc[key]) {
                      acc[key] = {
                        product_id: item.product_id,
                        product_code: item.product_code,
                        description: item.description,
                        color: item.color,
                        price: item.price,
                        sizes: {},
                        totalQuantity: 0,
                        subtotal: 0
                      };
                    }
                    acc[key].sizes[item.size] = item.quantity;
                    acc[key].totalQuantity += item.quantity;
                    acc[key].subtotal += item.quantity * item.price;
                    return acc;
                  }, {});

                return Object.values(groupedItems).map((group, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <div className="text-sm">
                        <div className="font-medium">{group.product_code}</div>
                        <div className="text-xs text-gray-500 line-clamp-1">{group.description}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {group.product_id && products.find(p => p.id === group.product_id)?.images?.length > 0 && (
                        <button
                          type="button"
                          className="w-12 h-12 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded"
                          onClick={() => {
                            const product = products.find(p => p.id === group.product_id);
                            const src = product?.images[product?.main_image_index || 0];
                            const alt = product?.description || "Imagen del producto";
                            if (src) {
                              openImagePreview(src, alt);
                            }
                          }}
                          aria-label="Ver imagen en grande"
                        >
                          <img
                            src={products.find(p => p.id === group.product_id)?.images[products.find(p => p.id === group.product_id)?.main_image_index || 0]}
                            alt={products.find(p => p.id === group.product_id)?.description}
                            className="w-full h-full object-cover rounded"
                            loading="lazy"
                          />
                        </button>
                      )}
                    </TableCell>
                    <TableCell>
                      <ColorBadge color={group.color} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 flex-wrap">
                        {Object.entries(group.sizes)
                          .sort(([sizeA], [sizeB]) => {
                            const sizeOrder = ["P", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL"];
                            return sizeOrder.indexOf(sizeA) - sizeOrder.indexOf(sizeB);
                          })
                          .map(([size, quantity]) => (
                          <div key={size} className="flex items-center gap-1">
                            <span className="text-xs font-medium">{size}:</span>
                            <Input
                              type="number"
                              min="0"
                              value={quantity}
                              onChange={async (e) => {
                                const newQuantity = Number(e.target.value);
                                
                                // Si la cantidad es 0, eliminar esta talla específica
                                if (newQuantity === 0) {
                                  setOrder((prev) => ({
                                    ...prev,
                                    items: prev.items.filter(item => 
                                      !(item.product_id === group.product_id && 
                                        item.color === group.color && 
                                        item.size === size)
                                    )
                                  }));
                                  return;
                                }
                                
                                // Verificar stock si el producto requiere stock
                                const product = products.find(p => p.id === group.product_id);
                                if (product?.venta_solo_stock) {
                                  try {
                                    const availableStock = await checkStockAvailability(group.product_id, group.color, size);
                                    
                                    if (newQuantity > availableStock) {
                                      toast({
                                        title: "Error",
                                        description: `Stock insuficiente. Solo hay ${availableStock} unidades disponibles`,
                                        variant: "destructive"
                                      });
                                      return;
                                    }
                                  } catch (error) {
                                    console.error('Error al verificar stock:', error);
                                    toast({
                                      title: "Error",
                                      description: "No se pudo verificar el stock del producto",
                                      variant: "destructive"
                                    });
                                    return;
                                  }
                                }

                                setOrder((prev) => {
                                  const updatedItems = [...prev.items];
                                  const itemIndex = updatedItems.findIndex(
                                    item => item.product_id === group.product_id && 
                                           item.color === group.color && 
                                           item.size === size
                                  );
                                  if (itemIndex !== -1) {
                                    updatedItems[itemIndex].quantity = newQuantity;
                                    updatedItems[itemIndex].pending_quantity = newQuantity;
                                  }
                                  return { ...prev, items: updatedItems };
                                });
                              }}
                              className="w-16 h-8 text-sm"
                            />
                          </div>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-sm text-gray-500">Total: {group.totalQuantity} uds.</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={group.price}
                          onChange={(e) => {
                            const newPrice = parseFloat(e.target.value);
                            setOrder((prev) => {
                              const updatedItems = prev.items.map(item => {
                                if (item.product_id === group.product_id && item.color === group.color) {
                                  return { ...item, price: newPrice };
                                }
                                return item;
                              });
                              return { ...prev, items: updatedItems };
                            });
                          }}
                          className="w-24"
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      {group.subtotal.toFixed(2)}€
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        onClick={() => {
                          setOrder(prev => ({
                            ...prev,
                            items: prev.items.filter(item => 
                              !(item.product_id === group.product_id && item.color === group.color)
                            )
                          }));
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ));
              })()}
            </TableBody>
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan="5" className="px-4 py-2 text-right font-medium">Total:</td>
                <td className="px-4 py-2 text-right font-medium">
                  {orderTotal.toFixed(2)}€
                </td>
                <td></td>
              </tr>
            </tfoot>
          </Table>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-medium mb-4">Firma del Cliente</h3>
        <div className="space-y-4">
          <div className="border rounded-lg p-4">
            <canvas
              ref={canvasRef}
              id="signature-canvas"
              name="signature-canvas"
              width={400}
              height={200}
              className="border rounded-lg cursor-crosshair touch-none"
              onPointerDown={startDrawing}
              onPointerMove={draw}
              onPointerUp={stopDrawing}
              onPointerLeave={stopDrawing}
              onPointerCancel={stopDrawing}
              aria-label="Área de firma"
            />
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={clearSignature}
            >
              Limpiar Firma
            </Button>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={navigateBackSafe}
          disabled={isSubmitting}
        >
          Cancelar
        </Button>
        <Button 
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Guardando..." : "Guardar Pedido"}
        </Button>
      </div>
      </form>
    </div>
  );
}
